import {StrictMode, useContext, useEffect} from 'react';
import {MessageFormatElement} from 'intl-messageformat-parser';
import {css, view} from '@sail/ui/deprecated';
import {
  MinimalHeader,
  DotComHeader,
  Header,
  useWidget,
  Widget,
  WidgetNext,
} from '@stripe-internal/conversations-ui';

import type {Profile} from '@stripe-internal/conversations-shared-dependencies';
import {isIframed} from './helpers/isIframed';
import {ConfigContext} from './Providers/ConfigProvider';
import {isDotComAudience} from './Screens/ChatScreen/conversationUtils';

import {AppProviders} from './Providers';
import {
  ScreenName,
  defaultScreenFor,
  useScreen,
} from './Providers/ScreenProvider';
import {Title} from './components/Title';
import {HeaderNext} from './components/HeaderNext';
import {useResumeConversation} from './useResumeConversation';
import {ErrorBoundary} from './components/ErrorBoundary';
import {CurrentScreen, UnrecoverableErrorScreen} from './Screens';
import {useProfile} from './Providers/ProfileProvider';
import {useHandleAuthFailure} from './Providers/ProfileProvider/HandleAuthFailureApiProvider';

function shouldUseNewDesign(profile: Profile) {
  return profile.featureFlags.universalHelpUseNewDesign;
}

const InnerApp = () => {
  const config = useContext(ConfigContext);
  const {profile} = useProfile();
  const {needsAuth} = useHandleAuthFailure();

  const {screen, transitionTo} = useScreen();
  const widget = useWidget();

  useEffect(() => {
    // This will return the correct screen to transition to
    // based on the profile's feature flags and whether a
    // session is required based on the config.
    const defaultScreen = defaultScreenFor(config, profile, needsAuth);

    const handleProfileLoaded = () => {
      if (
        screen.name === ScreenName.SessionRetry &&
        screen.name !== defaultScreen.name
      ) {
        transitionTo(defaultScreen);
      }
    };

    // Since the visibility callback below would only run on visibility
    // changes, this ensures we also check for the current visibility.
    handleProfileLoaded();

    const sub = widget.visibilityObservable().subscribe(() => {
      const visibility = widget.visibility();
      if (visibility === 'closed') {
        if (screen.name !== ScreenName.Blank) {
          transitionTo({
            name: ScreenName.Blank,
            // Hide header on blank screen if current screen does not have header
            showHeader: widget.header.isVisible(),
          });
        }
      } else if (visibility === 'open') {
        if (screen.name === ScreenName.Blank) {
          transitionTo(defaultScreen);
          return;
        }

        handleProfileLoaded();
      }
    });
    return () => sub.unsubscribe();
  }, [config, profile, needsAuth, screen.name, transitionTo, widget]);

  // this hook will open an existing conversation or RAC
  // if they're present, opening the widget if necessary.
  // these live above the widget in the tree, since the screen
  // is not rendered when the widget is closed.
  useResumeConversation();

  const content = (
    <ErrorBoundary fallback={<UnrecoverableErrorScreen />}>
      <CurrentScreen />
    </ErrorBoundary>
  );

  let widgetHeader = <Header defaultTitle={<Title />} />;
  if (config.viewMode === 'minimal') {
    widgetHeader = <MinimalHeader />;
  } else if (shouldUseNewDesign(profile)) {
    widgetHeader = <HeaderNext />;
  } else if (isDotComAudience(config.audience, config.entrypoint)) {
    widgetHeader = (
      <DotComHeader isCSAT={screen.name === ScreenName.DotComCSAT} />
    );
  }

  if (shouldUseNewDesign(profile)) {
    return <WidgetNext header={widgetHeader}>{content}</WidgetNext>;
  } else {
    return (
      <Widget header={widgetHeader} defaultTitle={<Title />}>
        {content}
      </Widget>
    );
  }
};

type Props = {
  locale?: string;
  messages?: Record<string, string> | Record<string, MessageFormatElement[]>;
};

export function App({locale = 'en-US', messages}: Props) {
  let height = '100%';
  if (isIframed()) {
    height = '100vh';
  }
  return (
    <StrictMode>
      <AppProviders locale={locale} messages={messages}>
        <view.div css={{height: css.value(height)}}>
          <InnerApp />
        </view.div>
      </AppProviders>
    </StrictMode>
  );
}
